import { QueryClient } from '@tanstack/react-query';
import { client } from '@/shared/api/generated/client.gen';
import { API_BASE_URL } from '@/shared/config';
import { useAuthStore } from '@/shared/lib/store/authStore';
import { logger } from '@/shared/lib';

/**
 * TanStack Query Client Configuration
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Configure the generated API client
 */
client.setConfig({
  baseUrl: API_BASE_URL,
});

/**
 * Request Interceptor - Inject fresh access token on every request
 */
client.interceptors.request.use((request) => {
  const token = useAuthStore.getState().tokens?.access_token;
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return request;
});

/**
 * Re-export the configured client
 */
export { client };

/**
 * WebSocket Client for real-time communication
 */
export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  constructor(url: string) {
    this.url = url;
  }

  connect(onMessage?: (data: any) => void, onError?: (error: Event) => void) {
    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        logger.success('WebSocket connected');
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onMessage?.(data);
        } catch (error) {
          logger.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        logger.info('WebSocket disconnected');
        this.reconnect(onMessage, onError);
      };

      this.ws.onerror = (error) => {
        logger.error('WebSocket error:', error);
        onError?.(error);
      };
    } catch (error) {
      logger.error('Error creating WebSocket connection:', error);
      onError?.(error as Event);
    }
  }

  private reconnect(onMessage?: (data: any) => void, onError?: (error: Event) => void) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      logger.debug(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

      setTimeout(() => {
        this.connect(onMessage, onError);
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  send(data: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}
